import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as keyboardShortcuts from "keyboardShortcuts";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import AccentInputView from "views/AccentInputView";
import PracticeContextView from "views/PracticeContextView";

interface State {
  inputValue: string;
  submitted: boolean;
  synonymCheck?:
    | {
        type: "checking";
      }
    | {
        type: "result";
        possibleSynonym: boolean;
      };
  hint?: {
    kind: "starts" | "ends";
    snippet: string;
  };
}

interface PersistedState {
  inputValue: string;
  submitted: boolean;
  hint?: State["hint"];
}

class TypingChallengeView extends preact.Component<
  practiceUtils.ChallengeProps,
  State
> {
  userWords: UserWordStore[];
  inputElement: HTMLInputElement;

  constructor(props: practiceUtils.ChallengeProps) {
    super(props);

    const persisted = props.persistedState as PersistedState | undefined;

    this.state = persisted
      ? {
          inputValue: persisted.inputValue,
          submitted: persisted.submitted,
          hint: persisted.hint,
        }
      : {
          inputValue: "",
          submitted: false,
        };

    // Skip registering the hint shortcut when remounting after edit into a
    // state where the user already revealed the hint or submitted an answer.
    if (!this.state.hint && !this.state.submitted) {
      keyboardShortcuts.registerHandler(
        "practicePage",
        keyboardShortcuts.keys.forwardSlash,
        () => {
          this.setState({
            hint: this.getHint(),
          });
          this.unregisterHintShortcut();
        },
        true,
        true,
        deviceDetect.info().mac ? "meta" : "ctrl"
      );
    }
  }

  getPersistedState(): PersistedState {
    return {
      inputValue: this.state.inputValue,
      submitted: this.state.submitted,
      hint: this.state.hint,
    };
  }

  render() {
    // Computed during render (not cached in state) so it reflects edits to the
    // word's context made mid-session, e.g. via the Edit Card modal.
    const clozeSentence = practiceUtils.getClozeSentence(
      this.props.userWord,
      this.props.selectedContextIndex
    );
    const isRtl =
      languageMap.map[this.props.userWord.state.value.language]?.direction ===
      "RTL";
    const hint = this.state.hint;

    return (
      <div>
        <h2>Type the missing word</h2>
        <p>
          <PracticeContextView
            clozeSentence={clozeSentence}
            rtl={
              languageMap.map[this.props.userWord.state.value.language]
                ?.direction === "RTL"
            }
            mode={
              this.state.submitted
                ? {
                    type: "complete",
                    ttsLanguage: this.props.userWord.state.value.language,
                    transliterationLanguage:
                      this.props.userWord.state.value.language,
                  }
                : { type: "blanked-out" }
            }
          />
        </p>
        <p>
          Meaning:{" "}
          <strong>{this.props.userWord.state.value.translation}</strong>
          {this.props.userWord.state.value.alternativeTranslations?.length
            ? `, ${this.props.userWord.state.value.alternativeTranslations.join(", ")}`
            : ""}
        </p>
        <div class="inputRow">
          <input
            ref={(input) => {
              if (input) {
                this.inputElement = input;
              }
            }}
            key={this.props.userWord.id}
            type="text"
            placeholder={`Type in ${
              languageMap.map[this.props.userWord.state.value.language].english
            }`}
            value={
              this.props.skipped
                ? this.props.userWord.state.value.word
                : this.state.inputValue
            }
            onInput={(event) => {
              this.setState({ inputValue: event.currentTarget.value });
              this.props.enableCheck(
                event.currentTarget.value.trim().length > 0
              );
            }}
            onKeyDown={(event) => {
              if (event.isComposing || event.keyCode === 229) {
                // Ignore if it's a composition event. This can happen when typing in languages like
                // Korean.
                return;
              }

              if (event.key === "Enter") {
                this.checkAnswer();
                event.stopPropagation();
                event.preventDefault();
              }
            }}
            disabled={this.state.submitted || this.props.skipped}
            autoCapitalize="off"
            lang={this.props.userWord.state.value.language}
          ></input>
        </div>
        <AccentInputView
          getInputElement={() => this.inputElement}
          language={this.props.userWord.state.value.language}
          disabled={this.props.skipped}
        />
        <div
          class={cn({
            hint: true,
            submitted: this.state.submitted,
          })}
        >
          {hint ? (
            <>
              <p>
                HINT: {hint.kind === "starts" ? "Starts with " : "Ends with "}"
                <bdi dir={isRtl ? "rtl" : "ltr"}>
                  {hint.kind === "starts"
                    ? `${hint.snippet}...`
                    : `...${hint.snippet}`}
                </bdi>
                "
              </p>
              <p>
                <button
                  onClick={async () => {
                    await this.setState({ inputValue: "" });
                    // Maybe shouldn't say "Incorrect" in this case - should be neutral
                    this.checkAnswer();
                  }}
                >
                  Show Answer
                </button>
              </p>
            </>
          ) : (
            <button
              disabled={this.props.skipped}
              onClick={() => {
                this.setState({
                  hint: this.getHint(),
                });
                this.unregisterHintShortcut();
                this.inputElement.focus();
              }}
            >
              Show Hint
            </button>
          )}
        </div>
        {this.renderSynonymCheck()}
      </div>
    );
  }

  componentDidMount(): void {
    this.inputElement.focus();
  }

  unregisterHintShortcut() {
    keyboardShortcuts.unregisterHandler(
      "practicePage",
      keyboardShortcuts.keys.forwardSlash,
      true,
      deviceDetect.info().mac ? "meta" : "ctrl"
    );
  }

  componentWillUnmount(): void {
    this.unregisterHintShortcut();
  }

  getHint(): NonNullable<State["hint"]> {
    const word = this.props.userWord.state.value.word;

    if (!word) {
      throw Error("No word to get hint for");
    }

    // Show "starts with" hint 2/3 of the time
    if (Math.random() < 0.67) {
      return {
        kind: "starts",
        snippet: word.substring(0, Math.floor(word.length / 2.5)),
      };
    } else {
      return {
        kind: "ends",
        snippet: word.substring(Math.ceil(word.length / 2)),
      };
    }
  }

  renderSynonymCheck() {
    if (!this.state.synonymCheck) {
      return null;
    }

    switch (this.state.synonymCheck?.type) {
      case "checking":
        return (
          <p>
            The original answer was: "{this.props.userWord.state.value.word}".
            Checking if your answer is a valid synonym...
          </p>
        );

      case "result":
        return this.state.synonymCheck.possibleSynonym ? (
          <>
            <p>
              Your answer might be a valid synonym of "
              {this.props.userWord.state.value.word}". Accept "
              {this.state.inputValue}" as a synonym?
            </p>
            <p>
              <button
                onClick={() => {
                  this.setState({ synonymCheck: undefined });

                  // Add to accepted synonyms for this user word (alternativeWords)
                  const alternativeWords = [
                    ...(this.props.userWord.state.value.alternativeWords ?? []),
                  ];
                  alternativeWords.push(this.state.inputValue);
                  this.props.userWord.save(
                    {
                      alternativeWords,
                    },
                    { patch: true }
                  );

                  this.props.completedCallback({
                    type: "show-feedback",
                    correct: "almost",
                  });
                  this.setState({ submitted: true });
                }}
              >
                Yes
              </button>
              <button
                onClick={() => {
                  this.setState({ synonymCheck: undefined });
                  this.reportIncorrect();
                }}
              >
                No
              </button>
            </p>
          </>
        ) : (
          <p>Your answer is not a valid synonym.</p>
        );
    }
  }

  getResult():
    | { type: "correct"; original: boolean }
    | { type: "watch-accent"; original: boolean; closestAnswer?: string }
    | { type: "typo"; original: boolean; closestAnswer?: string }
    | { type: "incorrect" } {
    const originalWordGrade = practiceUtils.gradeTyping(
      this.props.userWord.state.value.word,
      this.state.inputValue
    );

    const alternativeWordGrades = (
      this.props.userWord.state.value.alternativeWords ?? []
    ).map((word) => practiceUtils.gradeTyping(word, this.state.inputValue));

    // Check for perfect match
    if (originalWordGrade === "correct") {
      return { type: "correct", original: true };
    }
    if (alternativeWordGrades.some((grade) => grade === "correct")) {
      return { type: "correct", original: false };
    }

    // Check for accent mismatch
    if (originalWordGrade === "watch-accent") {
      return { type: "watch-accent", original: true };
    }
    const alternativeWordMatchIndex = alternativeWordGrades.findIndex(
      (alternativeWordGrade) => alternativeWordGrade === "watch-accent"
    );

    if (alternativeWordMatchIndex !== -1) {
      return {
        type: "watch-accent",
        original: false,
        closestAnswer:
          this.props.userWord.state.value.alternativeWords[
            alternativeWordMatchIndex
          ],
      };
    }

    // Check for typo
    if (originalWordGrade === "typo") {
      return { type: "typo", original: true };
    }

    const alternativeWordTypoIndex = alternativeWordGrades.findIndex(
      (alternativeWordGrade) => alternativeWordGrade === "typo"
    );

    if (alternativeWordTypoIndex !== -1) {
      return {
        type: "typo",
        original: false,
        closestAnswer:
          this.props.userWord.state.value.alternativeWords[
            alternativeWordTypoIndex
          ],
      };
    }

    // If we've reached here, then we haven't been able to find a match
    return { type: "incorrect" };
  }

  async checkAnswer() {
    const userWord = this.props.userWord;
    const result = this.getResult();

    switch (result.type) {
      case "correct":
        this.props.completedCallback({
          type: "show-feedback",
          correct: "yes",
          message: result.original
            ? undefined
            : {
                title: "Correct alternative!",
                body: `Original word: "${userWord.state.value.word}"`,
              },
        });
        this.props.reportRecallEase(
          this.state.hint === undefined ? (result.original ? 5 : 4) : 3,
          this.state.hint === undefined ? 1 : 0.5
        );
        this.setState({ submitted: true });

        if (stores.user.speakingMode.value) {
          practiceUtils.playTTS(
            userWord.state.value.language,
            userWord.state.value.word
          );
        }
        break;

      case "watch-accent":
        this.props.completedCallback({
          type: "show-feedback",
          correct: practiceUtils.getLeniency() === "strict" ? "almost" : "yes",
          message: result.original
            ? {
                title: `Watch your accents!`,
                body: `Correct answer: "${userWord.state.value.word}"`,
              }
            : {
                title: `Watch your accents!`,
                body: `Correct alternative: "${result.closestAnswer}". Original: "${userWord.state.value.word}"`,
              },
          showLeniencySettings: true,
        });
        this.props.reportRecallEase(
          practiceUtils.getLeniency() === "strict"
            ? 3
            : result.original
              ? 5
              : 4,
          1
        );
        this.setState({ submitted: true });
        if (stores.user.speakingMode.value) {
          practiceUtils.playTTS(
            userWord.state.value.language,
            result.closestAnswer ?? userWord.state.value.word // TODO: match capitalization?
          );
        }
        break;

      case "typo": {
        // Only the most lenient setting accepts a typo; otherwise it's a fail
        const typosAllowed = practiceUtils.getLeniency() === "allowTypos";
        this.props.completedCallback({
          type: "show-feedback",
          correct: typosAllowed ? "almost" : "no",
          message: result.original
            ? {
                title: `Watch the typo!`,
                body: `Correct answer: "${userWord.state.value.word}"`,
              }
            : {
                title: `Watch the typo!`,
                body: `Correct alternative: "${result.closestAnswer}". Original: "${userWord.state.value.word}"`,
              },
          showLeniencySettings: true,
        });
        this.props.reportRecallEase(typosAllowed ? 3 : 2, 1);
        this.setState({ submitted: true });
        if (stores.user.speakingMode.value) {
          practiceUtils.playTTS(
            userWord.state.value.language,
            result.closestAnswer ?? userWord.state.value.word // TODO: match capitalization?
          );
        }
        break;
      }

      case "incorrect":
        // XXX this isn't working reliably enough. too many false positives
        // this.checkForSynonym();

        this.reportIncorrect();
        if (stores.user.speakingMode.value) {
          practiceUtils.playTTS(
            userWord.state.value.language,
            userWord.state.value.word
          );
        }
        break;
    }
  }

  async checkForSynonym() {
    const userWord = this.props.userWord;

    await this.setState({ synonymCheck: { type: "checking" } });

    readlangClient.request(
      "GET",
      "/api/smartDictionary/askAI",
      {
        prompt: `Is "${this.state.inputValue}" a valid synonym of "${userWord.state.value.word}", and mean "${userWord.state.value.translation}", and is spelled correctly, and is grammatically correct within the following context:
          
> ${practiceUtils
          .getClozeSentence(userWord, this.props.selectedContextIndex)
          .map((part) => part.text)
          .join("")}?
          
If it satisfies all four criteria just answer "yes", otherwise just answer "no".`,
        model: "gpt-4o-mini",
      },
      (response) => {
        console.log("Synonym check response", response);
        const potentialSynonym = response.toLowerCase().includes("yes");

        if (potentialSynonym) {
          this.setState({
            synonymCheck: {
              type: "result",
              possibleSynonym: response.toLowerCase().includes("yes"),
            },
          });
        } else {
          this.setState({
            synonymCheck: undefined,
          });
          this.reportIncorrect();
        }
      },
      () => {
        // Clear the "Checking if your answer is a valid synonym..." indicator
        this.setState({ synonymCheck: undefined });
        this.reportIncorrect();
      },
      { dataType: "text" }
    );
  }

  reportIncorrect() {
    if (this.state.inputValue.trim().length === 0) {
      // User didn't even try, so don't say Incorrect, and offer the typing mode toggle
      this.props.completedCallback({
        type: "show-feedback",
        correct: "no",
        message: {
          title: "Skipped",
          body: `The correct answer is "${this.props.userWord.state.value.word}"`,
        },
        showTypingModeToggle: true,
      });

      this.props.reportRecallEase(2, 1);
      this.setState({ submitted: true });
      return;
    }

    this.props.completedCallback({
      type: "show-feedback",
      correct: "no",
      message: {
        title: "Incorrect",
        body: `The correct answer is "${this.props.userWord.state.value.word}"`,
      },
      showLeniencySettings: true,
    });
    this.props.reportRecallEase(2, 1);
    this.setState({ submitted: true });
  }
}

export default TypingChallengeView;
